import React from "react";
import { computeGaugeDisplay } from "./gaugeRange";

export interface MultiGaugeItem {
  label: string;
  value: number;
  unit: string;
  /** Known-good operating bound, if any — a soft hint, not a hard clamp. */
  min?: number;
  max?: number;
  /** Recent history used to size the dynamic scale. */
  points?: { t: number; v: number }[];
  /** Timestamp (ms since epoch) of this item's most recent reading. */
  lastUpdate?: number;
  color: string;
}

interface MultiGaugeProps {
  title: string;
  items: MultiGaugeItem[];
  icon?: React.ReactNode;
  /** Optional cumulative total shown as a footer (e.g. air consumed). */
  total?: {
    value: number;
    unit: string;
    label?: string;
  };
}

/**
 * A card showing several related metrics (e.g. compressed-air pressure +
 * volume) as stacked linear meters — one bar per metric, each against its own
 * min/max range — so combined KPI groups read as one card without implying a
 * closed loop.
 */
const MultiGauge: React.FC<MultiGaugeProps> = ({
  title,
  items,
  icon,
  total,
}) => {
  return (
    <div className="h-full rounded-2xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200/70 dark:border-gray-700 p-5 flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <h4 className="text-base font-semibold text-gray-800 dark:text-gray-100">
          {title}
        </h4>
        <span
          className="flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-700 shadow-sm flex-shrink-0"
          style={{ color: items[0]?.color }}
        >
          {icon ?? (
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 15l4-5 4 3 5-7 5 6"
              />
            </svg>
          )}
        </span>
      </div>

      <div className="flex-1 flex flex-col justify-start gap-3">
        {items.map((item) => {
          const display = computeGaugeDisplay(
            item.value,
            item.min,
            item.max,
            item.points,
          );
          return (
          <div key={item.label}>
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {item.label}
              </span>
              <span className="flex items-baseline gap-2">
                {item.lastUpdate !== undefined && (
                  <span className="text-[11px] text-gray-400 dark:text-gray-500">
                    {new Date(item.lastUpdate).toLocaleTimeString()}
                  </span>
                )}
                <span className="flex items-baseline gap-1">
                  <span
                    className="text-lg font-bold"
                    style={{ color: item.color }}
                  >
                    {item.value.toFixed(1)}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {item.unit}
                  </span>
                </span>
              </span>
            </div>
            <div
              className="h-2 rounded-full overflow-hidden"
              style={{ backgroundColor: "var(--border-primary, #e5e7eb)" }}
            >
              <div
                className="h-full rounded-full transition-[width] duration-300 ease-in-out"
                style={{
                  width: `${display.fillPct}%`,
                  backgroundColor: item.color,
                }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              <span>{display.leftLabel}</span>
              <span>{display.rightLabel}</span>
            </div>
          </div>
          );
        })}
      </div>

      {total && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
            {total.label ?? "Total"}
          </span>
          <span className="flex items-baseline gap-1 flex-shrink-0">
            <span className="text-lg font-bold text-gray-800 dark:text-gray-100">
              {total.value.toLocaleString(undefined, {
                maximumFractionDigits: 1,
              })}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {total.unit}
            </span>
          </span>
        </div>
      )}
    </div>
  );
};

export default MultiGauge;
